import React, { useCallback } from 'react';
import { identity, pick, prop } from 'ramda';

import { useLaunchGame } from '@core/js/hooks/gameLauncher/hooks/useLaunchGame';
import { useAppSelector } from '@core/js/store/hooks/useAppSelector';
import { useDeviceSelector } from '@core/js/store/hooks/useDeviceSelector';
import { useUserSelector } from '@core/js/store/hooks/useUserSelector';

import { GAME_EVENTS } from '@interlayer/js/app/components/main/Games/constants';
import { useGameEventDispatch } from '@interlayer/js/app/components/main/Games/hooks/useGameEventDispatch';
import { useGamesSelector } from '@interlayer/js/app/components/main/Games/store/context';
import { DEFAULT_ANALITICS_FEED_NAMES } from '@interlayer/js/app/hooks/analiticsSubscribe/constants';
import { useCheckOccupation, useCheckOccupationTags } from '@interlayer/js/app/hooks/user/hooks/useCheckOccupation';
import { getIsUserVerified, useIsUserVerified } from '@interlayer/js/app/hooks/user/hooks/useIsUserVerified';
import { useLoginPopup } from '@interlayer/js/components/popups/LoginPopup/hooks';
import { IPlayerTag, IUserDetails } from '@interlayer/js/hooks/user/store/buzz/types';
import { openWindow } from '@interlayer/js/utils/url.util';

import { htcmdService } from 'js/services/htcmd.service';

import { useGetBingoQueryUrl } from './hooks/useGetBingoQuerryUrl';
import { AUTO_GAME_TYPES, GAME_LAUNCH_TYPE } from './constants';
import { TUseAutoGameLaunch, TUseGameLaunch } from './types';

export const useGameLaunch = ({
    code = '',
    mode = 1,
    gameType,
    bingoGameType,
    gameLaunchType = GAME_LAUNCH_TYPE.Default,
    cb = identity,
    feedName,
    ...other
}: TUseGameLaunch) => {
    const launchGame = useLaunchGame();
    const showLoginPopup = useLoginPopup();
    const { gamesConfiguration, isWrapperApp } = useAppSelector(pick(['gamesConfiguration', 'isWrapperApp']));
    const { feed: { name: gameFeedName = '' } = {} } = useGamesSelector();
    const isUserVerified = useIsUserVerified();
    const gameEventDispatch = useGameEventDispatch();
    const checkOccupation = useCheckOccupation({ shouldCheckRequested: false });
    const checkOccupationTags = useCheckOccupationTags({ shouldCheckRequested: true, shouldCheckForce: true });
    const { isLoggedIn, userName: userNameData, sessionToken: token } = useUserSelector(pick(['isLoggedIn', 'userName', 'sessionToken']));
    const getBingoQueryUrl = useGetBingoQueryUrl(code);

    const handlerLaunchGame = useCallback(async (dataGame, userName, sessionToken) => {
        launchGame({
            ...dataGame,
            ...GAME_LAUNCH_TYPE.BingoRoom === gameLaunchType && {
                queryString: await getBingoQueryUrl(dataGame, userName, sessionToken)
            }
        });
        cb(dataGame);

        gameEventDispatch({
            name: GAME_EVENTS.GAME_LAUNCH,
            data: {
                gameType,
                gameCode: code,
                feedName: feedName || gameFeedName || DEFAULT_ANALITICS_FEED_NAMES.unknown,
                gameName: gamesConfiguration[code]?.name
            }
        });
    }, [
        launchGame,
        gameLaunchType,
        getBingoQueryUrl,
        cb,
        gameEventDispatch,
        gameType,
        code,
        feedName,
        gameFeedName,
        gamesConfiguration
    ]);

    const handlerShowLogin = useCallback((args) => {
        showLoginPopup({
            onClose: () => cb({ isOnClose: true }),
            onLogin: (userDetails?: IUserDetails, sessionToken?: string, playerTags?: Record<string, IPlayerTag>) => {
                if (userDetails && getIsUserVerified(userDetails.ageVerificationStatus) && !checkOccupationTags(playerTags)) {
                    return handlerLaunchGame(args, userDetails.username, sessionToken);
                }

                cb(args);
            }
        });
    }, [showLoginPopup, cb, checkOccupationTags, handlerLaunchGame]);

    return useCallback((data?: React.MouseEvent | Record<string, unknown>) => {
        let args = { code, mode, gameLaunchType, bingoGameType, ...other };

        if (!(data instanceof Event)) {
            args = { ...args, ...data };
        }

        if (htcmdService.isEnabled) {
            htcmdService.launchgame({ code, mode, bingoGameType });
            return;
        }

        if (isLoggedIn) {
            if (isUserVerified && !checkOccupation()) {
                return handlerLaunchGame(args, userNameData, token);
            }

            return cb();
        }

        if (isWrapperApp) {
            openWindow(`/igaming/${code}`, '_self');
            return;
        }

        handlerShowLogin(args);
    }, [
        code,
        mode,
        gameLaunchType,
        bingoGameType,
        other, isLoggedIn,
        isWrapperApp,
        handlerShowLogin,
        isUserVerified,
        cb,
        handlerLaunchGame,
        userNameData,
        token,
        checkOccupation
    ]);
};

export const useAutoGameLaunch = ({
    gameType,
    mobileGameType,
    gameCode,
    mobileGameCode,
    bingoGameType,
    cb,
    feedName
}: TUseAutoGameLaunch = {}) => {
    const isMobile = useDeviceSelector(prop('isMobile'));
    const checkGameType = isMobile && mobileGameType ? mobileGameType : gameType;
    const code = isMobile && mobileGameCode ? mobileGameCode : gameCode;

    let params;

    switch (checkGameType) {
        case AUTO_GAME_TYPES.BingoRoom:
            params = {
                gameLaunchType: GAME_LAUNCH_TYPE.BingoRoom,
                code,
                bingoGameType
            };
            break;
        default:
            params = {
                gameLaunchType: GAME_LAUNCH_TYPE.Default,
                desktopCode: gameCode,
                code
            };
    }

    return useGameLaunch({ ...params, cb, gameType, feedName });
};

export const useGameCodeLaunch = (gameCode: string) => {
    const game = useAppSelector((state) => state.gamesConfiguration?.[gameCode]);

    return useAutoGameLaunch(game);
};
